/**
 * W&W (04-11-2026): keuzetab voor de "Add to bag"-knop op productkaarten.
 * Markup: snippets/ww-add-to-bag.liquid.
 *
 * Producten zonder keuze: de klik gaat ongemoeid naar de echte Horizon-knop.
 * Producten met keuze: de eerste klik opent de tab (een stap per optie met meer dan een
 * waarde). Na de laatste keuze wordt de variant in het formulier gezet en de knop alsnog
 * doorgeklikt, zodat product-form.js toevoegt en de cart drawer openklapt zoals op de PDP.
 */
(() => {
  if (window.__wwAtb) return;
  window.__wwAtb = true;

  const CLOSE_ICON =
    '<svg viewBox="0 0 14 14" aria-hidden="true" focusable="false"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

  /** @type {HTMLElement | null} */
  let openRoot = null;

  const esc = (s) =>
    String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  function getData(root) {
    if (!root.__wwAtbData) {
      const node = root.querySelector('script[data-ww-atb-json]');
      try {
        root.__wwAtbData = JSON.parse(node ? node.textContent : 'null');
      } catch (e) {
        root.__wwAtbData = null;
      }
    }
    return root.__wwAtbData;
  }

  /** Opties met meer dan een waarde, in de volgorde van het product. */
  function getSteps(data) {
    return data.options
      .map((name, index) => {
        const values = [];
        for (const v of data.variants) {
          if (!values.includes(v.o[index])) values.push(v.o[index]);
        }
        return { name, index, values };
      })
      .filter((step) => step.values.length > 1);
  }

  function matches(variant, selection) {
    return Object.keys(selection).every((i) => variant.o[i] === selection[i]);
  }

  function setLabel(root, text) {
    const label = root.querySelector('[data-ww-atb-label]');
    if (label) label.textContent = text;
  }

  function chooseText(name) {
    return 'Choose a ' + String(name).toLowerCase();
  }

  function render(root) {
    const state = root.__wwAtbState;
    const step = state.steps[state.step];
    const panel = root.querySelector('[data-ww-atb-panel]');
    if (!panel || !step) return;

    const buttons = step.values
      .map((value) => {
        const selection = Object.assign({}, state.selection, { [step.index]: value });
        const available = state.data.variants.some((v) => v.a && matches(v, selection));
        return (
          '<button type="button" class="ww-atb__value" data-ww-atb-value="' +
          esc(value) +
          '"' +
          (available ? '' : ' disabled aria-disabled="true"') +
          '>' +
          esc(value) +
          '</button>'
        );
      })
      .join('');

    panel.innerHTML =
      '<div class="ww-atb__head"><p class="ww-atb__title">Select ' +
      esc(step.name) +
      '</p><button type="button" class="ww-atb__close" data-ww-atb-close aria-label="Close">' +
      CLOSE_ICON +
      '</button></div><div class="ww-atb__values' +
      (step.values.length > 5 ? ' ww-atb__values--grid' : '') +
      '" role="group" aria-label="' +
      esc(step.name) +
      '">' +
      buttons +
      '</div>';
    panel.setAttribute('aria-label', 'Select ' + step.name);
    setLabel(root, chooseText(step.name));
  }

  function open(root) {
    const data = getData(root);
    if (!data) return false;
    if (openRoot && openRoot !== root) close(openRoot);

    const steps = getSteps(data);
    if (!steps.length) return false;

    // Opties met maar een waarde liggen vast.
    const selection = {};
    data.options.forEach((name, index) => {
      if (!steps.some((s) => s.index === index)) selection[index] = data.variants[0].o[index];
    });

    root.__wwAtbState = { data, steps, step: 0, selection };
    const panel = root.querySelector('[data-ww-atb-panel]');
    panel.hidden = false;
    panel.setAttribute('role', 'dialog');
    root.classList.add('is-open');
    openRoot = root;
    render(root);

    const first = panel.querySelector('.ww-atb__value:not([disabled])');
    if (first && document.documentElement.dataset.wwInput === 'keyboard') first.focus();
    return true;
  }

  function close(root) {
    const panel = root.querySelector('[data-ww-atb-panel]');
    if (panel) {
      panel.hidden = true;
      panel.innerHTML = '';
    }
    root.classList.remove('is-open');
    root.__wwAtbState = null;
    setLabel(root, 'Add to bag');
    if (openRoot === root) openRoot = null;
  }

  function pick(root, value) {
    const state = root.__wwAtbState;
    if (!state) return;
    const step = state.steps[state.step];
    state.selection[step.index] = value;

    if (state.step < state.steps.length - 1) {
      state.step += 1;
      render(root);
      return;
    }

    const variant = state.data.variants.find((v) => v.a && matches(v, state.selection));
    if (!variant) return;

    const input = root.querySelector('input[name="id"]');
    const button = root.querySelector('button[name="add"]');
    if (!input || !button) return;

    input.value = String(variant.id);
    const container = button.closest('add-to-cart-component');
    if (container && variant.m) container.setAttribute('data-product-variant-media', variant.m);

    close(root);
    root.__wwAtbPass = true;
    button.click();
  }

  // Capture-fase: komt voor de handlers van Horizon, zodat de eerste klik alleen de tab opent.
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const root = target.closest('[data-ww-atb]');

      if (openRoot && openRoot !== root) close(openRoot);
      if (!root) return;

      if (target.closest('[data-ww-atb-panel]')) {
        event.preventDefault();
        event.stopPropagation();
        if (target.closest('[data-ww-atb-close]')) {
          close(root);
          return;
        }
        const valueButton = target.closest('[data-ww-atb-value]');
        if (valueButton && !valueButton.disabled) pick(root, valueButton.getAttribute('data-ww-atb-value'));
        return;
      }

      const addButton = target.closest('button[name="add"]');
      if (!addButton || !root.hasAttribute('data-ww-atb-choose')) return;

      if (root.__wwAtbPass) {
        root.__wwAtbPass = false;
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      if (root.classList.contains('is-open')) {
        close(root);
      } else if (!open(root)) {
        // Geen bruikbare keuzedata: gewoon toevoegen met de standaardvariant.
        root.__wwAtbPass = true;
        addButton.click();
      }
    },
    true
  );

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && openRoot) {
      const root = openRoot;
      close(root);
      root.querySelector('button[name="add"]')?.focus();
    }
  });
})();
